import * as _ from 'lodash';
import * as moment from 'moment';
import * as Autolinker from 'autolinker';

import { Injectable } from '@angular/core';
import { UiService } from '../../../core/services/core.ui.service';
import { UserService } from '../../../core/services/core.user.service';
import { DateGfToDatePipe } from '../../../core/pipes/date/date.gf-to-date.pipe';
import { DateGfDatePipe } from '../../../core/pipes/date/date.gf-date.pipe';
import { NotificationService } from '../../../core/services/core.notification.service';
import { createTILinks, getTIPreviewUrls } from '../../../core/pipes/tracker-item/ti-links.functions';
import { createGitLinks, getGitPreviewUrls } from '../../../core/pipes/git-links/git-links.functions';
import { DomSanitizer } from '@angular/platform-browser';
import { isMarkdownBody } from '../../../core/services/message-body-format';
import { escapeHtml } from '../../../core/services/core.utils.service';
import { maskCodeRegions, findSoundToken } from './chat-body.functions';

@Injectable()
export class ChatRenderService {
    constructor(
        private uiService: UiService,
        private userService: UserService,
        private gfToDatePipe: DateGfToDatePipe,
        private gfDatePipe: DateGfDatePipe,
        private notificationService: NotificationService,
        private _sanitizer: DomSanitizer
    ) { }

    private gfToDate = (s) => this.gfToDatePipe.transform(s);
    private gfDate = (s, f) => this.gfDatePipe.transform(s, f);
    private userRoleSlugs: string[] = [];
    private soundNames: string[] = [];

    setUserRoleSlugs = (slugs: string[]) => {
        this.userRoleSlugs = slugs;
    }

    /**
     * Supply the project's available chat sounds, so a `[token]` in a message
     * can be told apart from ordinary bracket syntax. See findSoundToken().
     */
    setSoundNames = (names: string[]) => {
        this.soundNames = Array.isArray(names) ? names : [];
    }

    delimiterText = (idx: number, list: any) => {
        if (!Array.isArray(list)) return false;

        var date1 = this.getPreviousDate(idx, list);
        var date2 = this.gfToDate(list[idx]?.date);

        if (!date2) return false;
        if (!date1) return this.gfDate(date2, 'll');

        // Has the date changed?
        if (date1.toDateString() != date2.toDateString()) {
            return this.gfDate(date2, 'll');
        }

        // Has the hour changed?
        if (date1.getHours() != date2.getHours()) {
            return moment(date2).set({ 'minute': 0 }).format('LT');
        }

        // Not different enough?  Don't do anything
        return false;
    }

    getPreviousDate = (idx, list) => {
        for (var lastIdx = idx - 1; lastIdx > -1; lastIdx--) {
            var previousItem = list[lastIdx];
            var previousDate = this.gfToDate(previousItem?.date);
            if (previousDate !== null) return previousDate;
        }

        return new Date();
    }

    chatMessages = (item: any) => {
        return _.map(
            _.get(item, 'items', []),
            function (msg) {
                return {
                    trackby: 'chat-' + msg.id,
                    id: msg.id,
                    date: msg.postDate,
                    type: 'chat',
                    subtype: null,
                    user: msg.user,
                    body: msg.body,
                    // Must be carried across: this rebuilds the message object
                    // from the API payload, and dropping the flag would send
                    // every message down the legacy renderer regardless of how
                    // it was stored. Both the poll and the load-older-messages
                    // path come through here.
                    isMarkdown: msg.isMarkdown,
                    flags: msg.flags
                };
            }
        );
    }

    activityMessages = (item: any) => {
        var activityItems = _.get(item, 'items', []);
        var userChanges = this.getUserChangeMessages(activityItems);

        return _.map(
            // Filter out some of the user changes - they are not displayed
            _.without(activityItems, userChanges),
            function (msg: any) {
                return {
                    trackby: 'activity-' + msg.id,
                    id: msg.id,
                    date: msg.date,
                    type: 'activity',
                    subtype: msg.activityType,
                    section: msg.section,
                    refId: msg.refId,
                    user: msg.user,
                    body: msg.description,
                    activity: msg
                };
            }
        );
    }

    /**
     * `wall_post.comment` has no format column to carry across (unlike
     * `forum_message.flags`). Its content is a backend-authored lead-in plus,
     * for a mention post, the follow-up or details text exactly as the author
     * typed it -- which is raw Markdown since the editors switched over. So
     * every wall body is routed to gf-markdown-viewer, which renders Markdown,
     * passes legacy (TinyMCE-era) HTML through, and sanitises either one.
     */
    wallMessages = (item: any) => {
        return _.map(
            _.filter(
                _.get(item, 'items', []),
                (msg) => !this.isForumMessage(msg) && !this.isWikiMessage(msg)
            ),
            function (msg: any) {
                return {
                    trackby: 'wall-' + msg.id,
                    id: msg.id,
                    date: msg.date,
                    type: 'wall',
                    item: { refId: msg.itemRefId, section: msg.itemSection },
                    user: msg.user,
                    wall: msg,
                    body: msg.comment,
                    isMarkdown: true
                };
            }
        );
    }

    isForumMessage = (msg: any) => {
        return _.get(msg, 'itemSection', '') == 'forum';
    }

    isWikiMessage = (msg: any) => {
        var section = _.get(msg, 'itemSection', '');
        return section === 'wiki' || section === 'wikipage' || section === 'userwiki';
    }

    getDeletedMessageIds = (items: any = {}, projectId: any = 0) => {

        var candidates =
            // Outer filter to get only items with deleted arrays
            _.filter(
                // Inner filter to get only items of the right type, section and refId
                _.filter(
                    Object.values(items),
                    {
                        type: 'activity',
                        section: 'project',
                        refId: projectId ? projectId.toString() : ''
                    }
                ),
                function (item) {
                    return _.get(item, 'deleted.length', 0) > 0;
                }
            );

        return _.uniq(
            _.flatMap(
                candidates, 'deleted'
            )
        );
    }

    getUserChangeMessages = (item: any) => {
        return _.filter(
            _.get(item, 'items', []),
            function (msg) {
                if (_.get(msg, 'section', '') === 'forumthread') {
                    return _.includes(
                        ['view'],
                        _.get(msg, 'activityType', '')
                    );
                }
                return false;
            }
        );
    }

    getMentionMessages = (messages: any) => {
        return _.filter(
            messages,
            this.hasMention
        );
    }

    transformPollData = (items: any = {}, threadId = 0, projectId = 0) => {

        var messages = _.reduce(
            Object.values(items),
            (carry, item) => {
                var newitems = [];

                var type = _.get(item, 'type', '');
                var section = _.get(item, 'section', '');
                var refId = _.get(item, 'refId', 0);

                if (type == 'chat' && section == 'forumthread' && refId == threadId) {
                    newitems = this.chatMessages(item);
                }

                if (type == 'activity' &&
                    section == 'project' &&
                    refId == projectId) {
                    newitems = this.activityMessages(item);
                }

                if (type == 'wall') {
                    newitems = this.wallMessages(item);
                }

                return carry.concat(newitems);
            }, []
        );

        return _.sortBy(messages, 'date');
    }

    /**
     * Remove wall posts when an activity log entry for the same item exists.
     * Activity log has preference for display in chat.
     * Compares across both existing (accumulated) messages and incoming new items,
     * so deduplication works regardless of which poll cycle each arrived in.
     * Mention and reaction wall posts are never removed this way - they carry content
     * (the @-mention text, the reaction) that the generic activity entry for the same
     * item doesn't have; a reaction doesn't even write an activity entry of its own.
     */
    deduplicateActivityAndWall = (existingMessages: any[], newItems: any[]) => {
        var wallKey = (m) => _.get(m, 'item.section', '') + ':' + _.get(m, 'item.refId', '');
        var activityKey = (m) => (m.section || '') + ':' + (m.refId || '');
        var isMentionPost = (m) => /^(mentioned you in|added a reaction )/.test(_.get(m, 'body', ''));

        // Collect activity keys from new items
        var newActivityKeys = new Set(
            newItems.filter(m => m.type === 'activity' && m.section).map(activityKey)
        );

        // Collect activity keys from existing messages
        var existingActivityKeys = new Set(
            existingMessages.filter(m => m.type === 'activity' && m.section).map(activityKey)
        );

        // Remove existing wall posts that match new activity entries
        if (newActivityKeys.size > 0) {
            existingMessages = existingMessages.filter(m =>
                m.type !== 'wall' || !m?.item || isMentionPost(m) || !newActivityKeys.has(wallKey(m))
            );
        }

        // Remove new wall posts that match existing (or new) activity entries
        var allActivityKeys = new Set([...existingActivityKeys, ...newActivityKeys]);
        if (allActivityKeys.size > 0) {
            newItems = newItems.filter(m =>
                m.type !== 'wall' || !m?.item || isMentionPost(m) || !allActivityKeys.has(wallKey(m))
            );
        }

        return { existingMessages, newItems };
    }

    appendAndPrune = (current: any, added: any) => {

        var toAdd = (Array.isArray(added) ? added : []);

        if (Array.isArray(current)) {
            return _.concat(current, toAdd);
        }

        return toAdd;
    }

    deleteMessages = (messages: any, idsToDelete: any) => {

        if (!Array.isArray(messages)) return [];
        if (!Array.isArray(idsToDelete)) return messages;

        return _.filter(
            messages,
            function (msg) {
                return !_.includes(idsToDelete, _.get(msg, 'id', 0));
            }
        );
    }

    preRenderMessages = (messages: any, project: string = '') => {
        return _.map(
            messages,
            (msg: any) => this.preRenderMessage(msg, project)
        );
    }

    preRenderMessage = (msg: any, project: string = '') => {

        var msgType = _.get(msg, 'type', false);

        if (msgType === false) {
            return msg;
        }

        msg.isMarkdownBody = isMarkdownBody(msg);

        // Raw Markdown bodies are rendered (and sanitised, and TI/git-linked) by
        // gf-markdown-viewer.
        if (msg.isMarkdownBody) {
            if (msg.type !== 'activity') {
                msg.renders = this.extractLinkRenders(_.get(msg, 'body', ''), project);
            }
            return msg;
        }

        var body = _.get(msg, 'body', '');

        // Format sound messages
        var sound = this.getSound(body);
        if (sound) {
            body = _.replace(body, '[' + sound + ']', '<i>[' + sound + ']</i>');
        }
        msg.body = body;

        // Attach the renders object to the message,
        // and pre-format any links in the msg body
        return this.getLinkRenders(msg, project);
    }

    /**
     * Classify a URL into the embed card that should render beneath the bubble.
     *
     * Shared by both body formats so the match order (zoom, youtube, image,
     * video, then a generic preview) stays in exactly one place.
     *
     * @returns the render object, or null when the URL is not embeddable.
     */
    private classifyUrl = (url: string): any => {

        var zoomMatch = url.match(/^https:\/\/(.*\.)?zoom.us\/[js]\//);
        if (Array.isArray(zoomMatch) && zoomMatch.length) {
            return {
                type: 'zoom',
                url: url
            };
        }

        var youtubeId = url.match(/^(?:http(?:s)?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:(?:watch)?\?(?:.*&)?v(?:i)?=|(?:embed|v|vi|user)\/))([^\?&\"'>]+)/);
        if (Array.isArray(youtubeId) && youtubeId.length) {
            return {
                type: 'youtube',
                url: this._sanitizer.bypassSecurityTrustResourceUrl(`//www.youtube.com/embed/${ youtubeId[1] }?html5=1&rel=0&autohide=1`),
                id: youtubeId[1]
            };
        }

        var imageUrl = url.match(/(https?:\/\/(?:[a-z0-9\-]+\.)+[a-z]{2,6}(?:\/[^\/#?]+)+\.(?:jpe?g|gif|png|webp))/i);
        if (Array.isArray(imageUrl) && imageUrl.length) {
            return {
                type: 'image',
                mimeType: 'image/' + imageUrl[2],
                url: imageUrl[0]
            };
        }

        var videoUrl = url.match(/https?:\/\/(?:[a-z0-9\-]+\.)+[a-z]{2,6}(?:\/[^\/#?]+)+\.(avi|mp4|mpeg|webm)/i);
        if (Array.isArray(videoUrl) && videoUrl.length) {
            return {
                type: 'video',
                mimeType: 'video/' + videoUrl[1],
                url: videoUrl[0]
            };
        }

        return {
            type: 'preview',
            url: url
        };
    }

    /**
     * Autolinker options. Shared so that the legacy rewrite pass and the
     * Markdown extract-only pass agree on what counts as a URL.
     */
    private static readonly AUTOLINK_OPTIONS = {
        newWindow: true,
        twitter: false,
        hashtag: false,
        stripPrefix: false,
        urls: {
            schemeMatches: true,
            wwwMatches: true,
            tldMatches: false
        }
    };

    /**
     * Build the embed-card list for a body WITHOUT modifying it.
     */
    extractLinkRenders = (body: string, project: string = ''): any[] => {
        if (!body) {
            return [];
        }

        const found = [];
        const searchable = maskCodeRegions(body);

        _.forEach(
            Autolinker.parse(searchable, ChatRenderService.AUTOLINK_OPTIONS),
            (match: any) => {
                if (match.getType() != 'url') return;
                const render = this.classifyUrl(match.getUrl());
                if (render) {
                    found.push({ offset: match.getOffset(), render: render });
                }
            }
        );

        // A square-bracket ref ([#12345]) asks for an embed card as well as a
        // link. The viewer makes the link straight out of the rendered DOM, so
        // the card has to be collected from the raw body here -- the legacy
        // path gets it from the URL copy createTILinks splices into the text.
        // A git:<hash> ref gets a commit card the same way.
        _.forEach(
            [...getTIPreviewUrls(searchable), ...getGitPreviewUrls(searchable, project)],
            (ref: any) => {
                const render = this.classifyUrl(ref.url);
                if (render) {
                    found.push({ offset: ref.offset, render: render });
                }
            }
        );

        return _.map(_.sortBy(found, 'offset'), 'render');
    }

    getLinkRenders(msg: any, project: string = '') {

        var renders = [];

        // Keep the callback fn inside this fn,
        // so we can create the renders data
        // as a side-effect to the required return value.
        // NOTE: returning '' replaces the URL with nothing, so a bare URL is
        // REMOVED from the bubble text and shown only as the card below.
        const addLinkRender = (match: any) => {
            if (match.getType() != 'url') return true;

            const render = this.classifyUrl(match.getUrl());
            if (render) {
                renders.push(render);
            }
            return '';
        };

        // Convert [#12345] and #12345 notations to links.
        // `true` asks for the bare copy of the URL that [#12345] refs carry:
        // the Autolinker pass below removes it from the body again and turns
        // it into an embed card.
        msg.body = createTILinks(msg.body, msg.type, true);

        // Commit cards are collected before the refs are linked, since the
        // Autolinker pass below leaves existing anchors alone.
        const gitPreviews = getGitPreviewUrls(msg.body, project);

        // Convert git:<hash> refs to commit links, when we know the project.
        msg.body = createGitLinks(msg.body, project);

        // Auto-link URLs in msg body,
        // and also set the renders as a side-effect.
        if (msg.type !== 'activity') {
            msg.body = Autolinker.link(msg.body, {
                ...ChatRenderService.AUTOLINK_OPTIONS,
                replaceFn: addLinkRender
            });
            _.forEach(gitPreviews, (ref: any) => renders.push(this.classifyUrl(ref.url)));
            msg.renders = renders;
        }

        return msg;
    }

    getCurrentUser = () => {
        return this.userService.session.getUnixName();
    }

    isCurrentUser = (unixName: any) => {
        if ((!unixName) || (unixName == '')) return false;
        return this.getCurrentUser() == unixName;
    }

    getMentionTextRaw = (item: any) => {
        var name = _.get(item, 'unixName', '');
        if (typeof item === 'string') {
            name = item;
        }
        if (!name || name == '') return '';
        return '@' + name;
    }

    hasMention = (message: any) => {
        var current = _.get(this.userService.getUser(), 'unixName', '');

        var currentAt = this.getMentionTextRaw(current);
        var allAt = this.getMentionTextRaw('all');

        var body = _.get(message, 'body', '');

        if (body.indexOf(currentAt) != -1) {
            return true;
        } else if (body.indexOf(allAt) != -1) {
            var submitter = _.get(message, 'user.unixName', '');
            return (submitter !== current);
        }

        // Check if current user is mentioned via a role slug
        if (this.userRoleSlugs.length > 0) {
            const submitter = _.get(message, 'user.unixName', '');
            for (let i = 0; i < this.userRoleSlugs.length; i++) {
                const roleAt = this.getMentionTextRaw(this.userRoleSlugs[i]);
                if (roleAt && body.indexOf(roleAt) != -1) {
                    return (submitter !== current);
                }
            }
        }

        return false;
    }

    // TODO not adjusting for time zone!
    isRecent = (msg: any) => {

        if (!msg) return false;

        var now = new Date().getTime();
        var rawDate = this.gfToDate(
            _.get(msg, 'date', '')
        );

        var then = rawDate ? rawDate.getTime() : 0;
        var interval = 60 * 1000; // 60 seconds

        return (now - then < interval);
    }

    getMessageSounds = (messages: any) => {
        return _.compact(
            _.map(messages, this.getSound)
        );
    }

    /**
     * The sound a message is asking to play, or false.
     */
    getSound = (msg: any) => {
        var body = _.get(msg, 'body', '');
        return findSoundToken(body, this.soundNames);
    }

    /**
     * Body text for a notification toast.
     *
     * The growl path hands its message to bootstrap-notify, which inserts it as
     * HTML. Legacy bodies were escaped by the backend at write time, so that
     * was safe; raw Markdown bodies are not, and Markdown allows inline HTML.
     * Escaping here keeps the toast showing exactly what the author typed.
     *
     * Scoped to Markdown bodies so legacy notifications look as they do today.
     */
    private notificationText = (msg: any): string => {
        const body = _.get(msg, 'body', '');
        return isMarkdownBody(msg) ? escapeHtml(body) : body;
    }

    // NOTE: messages is the list of messages WITH mentions
    showNotifications = (forumName: any, messages: any) => {

        if (this.uiService.isOnFocus) return false;

        _.map(messages, (msg) => {

            if (!this.isRecent(msg)) return false;

            this.uiService.message.showInfo(
                _.get(msg, 'user.unixName', 'Nobody') + ': ' + this.notificationText(msg),
                'Chatroom: ' + forumName,
                {
                    notificationType: 'notificationsApi',
                    onClickCallback: () => {
                        this.notificationService.notifyAll(
                            'GFCHAT-USER-MENTION',
                            {
                                user: _.get(msg, 'user', {}),
                                message: _.get(msg, 'body', '(no message)'),
                                section: _.get(msg, 'section', 'project'),
                                refId: _.get(msg, 'refId', 0)
                            }
                        );
                    }
                }
            );
        });
    }

    playSoundByName = (soundName: any, allSounds: any) => {
        var sound = _.find(allSounds, { name: soundName });
        if (sound) {
            this.playSound(sound.url);
        }
    }

    playSound = (url: any) => {
        const audio = new Audio(url);
        audio.play().catch(e => console.warn('Playback failed:', e));
    }

    playNotifySound = () => {
        this.playSound("/client-app/plugins/chat/sounds/mention.mp3");
    }
}
